// frontend/src/hooks/useEquipment.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTank } from '@/contexts/TankContext';
import { equipmentApi } from '@/api/equipment';
import { CACHE_TIMES } from '@/config/cacheTimes';
import type { EquipmentCreate, EquipmentUpdate } from '@/types/equipment';

/**
 * Hook to fetch equipment categories.
 * Categories are static and rarely change, so we use a long staleTime.
 */
export function useEquipmentCategories() {
  return useQuery({
    queryKey: ['equipment-categories'],
    queryFn: () => equipmentApi.getCategories(),
    staleTime: CACHE_TIMES.CONFIG,
    meta: { persist: true },
  });
}

/**
 * Hook to fetch equipment list for the current tank.
 * Supports optional status and category filters.
 */
export function useEquipmentList(params?: { status?: string; category?: string }) {
  const { selectedTank } = useTank();

  return useQuery({
    queryKey: ['equipment', selectedTank?.id, params],
    queryFn: () => equipmentApi.list(selectedTank!.id, params),
    enabled: !!selectedTank?.id,
  });
}

/**
 * Hook to fetch a single equipment item.
 */
export function useEquipment(equipmentId: string) {
  const { selectedTank } = useTank();

  return useQuery({
    queryKey: ['equipment', selectedTank?.id, equipmentId],
    queryFn: () => equipmentApi.get(selectedTank!.id, equipmentId),
    enabled: !!selectedTank?.id && !!equipmentId,
  });
}

/**
 * Hook to create new equipment.
 * Invalidates equipment queries on success and shows notification.
 */
export function useCreateEquipment() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: EquipmentCreate) => equipmentApi.create(selectedTank!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment', selectedTank?.id] });
      notifications.show({
        title: 'Success',
        message: 'Equipment added successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to add equipment',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to update existing equipment.
 * Invalidates equipment queries on success and shows notification.
 */
export function useUpdateEquipment() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EquipmentUpdate }) =>
      equipmentApi.update(selectedTank!.id, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment', selectedTank?.id] });
      notifications.show({
        title: 'Success',
        message: 'Equipment updated successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update equipment',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to delete equipment.
 * Invalidates equipment queries on success and shows notification.
 */
export function useDeleteEquipment() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (equipmentId: string) => equipmentApi.delete(selectedTank!.id, equipmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment', selectedTank?.id] });
      notifications.show({
        title: 'Success',
        message: 'Equipment deleted',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete equipment',
        color: 'red',
      });
    },
  });
}
